import { idiomaNegocio, monedaNegocio } from './moneda'
import { zonaNegocio } from './zona'

/*
 * Los formateadores se arman a demanda y se guardan porque construir un `Intl`
 * no es gratis y estas funciones se llaman una vez por celda de tabla. La clave
 * incluye todo lo que puede cambiar —idioma, zona, decimales— para que cambiar
 * la región en Configuración tenga efecto sin recargar la página.
 */
const numeros = new Map<string, Intl.NumberFormat>()

function numero(decimales: number): Intl.NumberFormat {
    const idioma = idiomaNegocio()
    const clave = `${idioma}|${decimales}`
    let f = numeros.get(clave)

    if (!f) {
        f = new Intl.NumberFormat(idioma, {
            style: 'decimal',
            minimumFractionDigits: decimales,
            maximumFractionDigits: decimales,
        })
        numeros.set(clave, f)
    }

    return f
}

/*
 * Las fechas se muestran en la zona del local, no en la del equipo. Si no, la
 * misma venta aparece con una hora distinta en la caja que en el celular del
 * duenio, y un equipo con la zona del sistema mal puesta corre las fechas de
 * todo el sistema sin que nadie lo note.
 */
const cache = new Map<string, Intl.DateTimeFormat>()

function formateador(clave: string, opciones: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    const z = zonaNegocio()
    const idioma = idiomaNegocio()
    const completa = `${clave}|${idioma}|${z}`
    let f = cache.get(completa)

    if (!f) {
        f = new Intl.DateTimeFormat(idioma, { ...opciones, timeZone: z })
        cache.set(completa, f)
    }

    return f
}

/**
 * Un importe con el símbolo y los decimales de la moneda del negocio.
 *
 * Se llamaba `pesos`, y el nombre dejó de ser cierto el día que el sistema se
 * ofreció en Perú. Los decimales tampoco eran ciertos: estaban fijos en cero, y
 * con eso S/ 2,50 se mostraba «S/ 3».
 */
export function dinero(valor: number | null | undefined, simbolo?: string): string {
    if (valor === null || valor === undefined) return '—'

    const moneda = monedaNegocio()
    const signo = valor < 0 ? '-' : ''

    return `${signo}${simbolo ?? moneda.simbolo}${numero(moneda.decimales).format(Math.abs(valor))}`
}

/** Cantidad con los decimales que admita la unidad: 3 para kilos, 0 para piezas. */
export function cantidad(valor: number, decimales: number): string {
    return numero(decimales).format(valor)
}

export function fechaHora(valor: string | null | undefined): string {
    return valor
        ? formateador('fechaHora', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(valor))
        : '—'
}

/** Solo el dia. Para columnas donde la hora no aporta y solo hace envolver el texto. */
export function fecha(valor: string | null | undefined): string {
    return valor ? formateador('fecha', { dateStyle: 'short' }).format(new Date(valor)) : '—'
}

export function hora(valor: string | null | undefined): string {
    return valor ? formateador('hora', { timeStyle: 'short' }).format(new Date(valor)) : '—'
}
